import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnInit, Optional } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';

import { AppSettings } from '../../../app.settings';
import {
  ConfirmDialogComponent,
  ConfirmDialogModel,
} from '../../modal/confirmdialog/confirmdialog.component';
import {
  ManagedFeatureFlag,
  ManagedFeatureFlagApp,
  ManagedFeatureFlagCompanyOption,
  ManagedFeatureFlagTeamOption,
  ManagedFeatureFlagEvaluationResult,
  ManagedFeatureFlagPayload,
  ManagedFeatureFlagRule,
  ManagedFeatureFlagRuleEffect,
  ManagedFeatureFlagsService,
} from '../../../services/managed-feature-flags/managed-feature-flags.service';
import { Partner, PartnersService } from '../../../services/partners/partners.service';
import { SessionService } from '../../../services/session/session.service';
import { UsersService } from '../../../services/users/users.service';

interface PartnerSearchResult {
  id: string;
  name: string;
  searchText: string;
}

interface CompanySearchResult {
  id: string;
  name: string;
  searchText: string;
}

interface TeamSearchResult {
  id: string;
  name: string;
  searchText: string;
}

interface ManagedFeatureFlagRuleEditor {
  id: string;
  effect: ManagedFeatureFlagRuleEffect;
  teamIds: string[];
  excludeTeamIds: string[];
  teamSearchQuery: string;
  teamSearchResults: TeamSearchResult[];
  manualTeamId: string;
  selectedTeamId: string;
  selectedTeamLabel: string;
  companyIds: string[];
  excludeCompanyIds: string[];
  partnerIds: string[];
  excludePartnerIds: string[];
  statesText: string;
  excludeStatesText: string;
  partnerSearchQuery: string;
  partnerSearchResults: PartnerSearchResult[];
  manualPartnerId: string;
  selectedPartnerId: string;
  selectedPartnerLabel: string;
  companySearchQuery: string;
  companySearchResults: CompanySearchResult[];
  manualCompanyId: string;
  selectedCompanyId: string;
  selectedCompanyLabel: string;
}

interface ManagedFeatureFlagEditor {
  id?: string;
  key: string;
  name: string;
  description: string;
  appTargets: ManagedFeatureFlagApp[];
  isActive: boolean;
  defaultEnabled: boolean;
  includeUserIds: string[];
  excludeUserIds: string[];
  rules: ManagedFeatureFlagRuleEditor[];
}

interface PreviewContextForm {
  app: ManagedFeatureFlagApp;
  userId: string;
  teamId: string;
  companyId: string;
  partnerId: string;
  state: string;
}

interface UserSearchResult {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

interface ManagedFeatureFlagTransferDocument {
  format: 'managed-feature-flag-transfer';
  version: 1;
  source: {
    backend: string;
    environment: string;
    exportedAt: string;
    clearUserOverrides: boolean;
    clearConditionalRules: boolean;
  };
  flag: ManagedFeatureFlagPayload;
}

type PreviewMode = 'draft' | 'saved';
type ManagedFeatureFlagsSectionKey = 'importExport' | 'userOverrides' | 'conditionalRules' | 'evaluationPreview';

interface PreviewFlagRecord {
  key: string;
  appTargets: ManagedFeatureFlagApp[];
  isActive: boolean;
  defaultEnabled: boolean;
  userOverrides: ManagedFeatureFlagPayload['userOverrides'];
  rules: ManagedFeatureFlagRule[];
}

const DEFAULT_SCHEMA_VERSION = 1;

@Component({
  selector: 'app-managed-feature-flags',
  templateUrl: './managed-feature-flags.component.html',
  styleUrls: ['./managed-feature-flags.component.scss'],
})
export class ManagedFeatureFlagsComponent implements OnInit {
  protected connectedBackend = AppSettings.getEndpoint();
  protected connectedEnvironment = AppSettings.getReleaseVersion();
  protected flags: ManagedFeatureFlag[] = [];
  protected filteredFlags: ManagedFeatureFlag[] = [];
  protected selectedFlagId = '';
  protected loading = false;
  protected loadFailed = false;
  protected saving = false;
  protected deleting = false;
  protected previewLoading = false;
  protected previewMode: PreviewMode = 'draft';
  protected previewResult: ManagedFeatureFlagEvaluationResult | null = null;
  protected partnerSearchResultsLimit = 10;
  protected userSearchLoading = false;
  protected userSearchResults: UserSearchResult[] = [];
  protected userSearchQuery = '';
  protected manualUserOverrideId = '';
  protected selectedUserOverrideId = '';
  protected selectedUserOverrideLabel = '';
  protected userSearchRequestId = 0;
  protected exportBlob = '';
  protected importBlob = '';
  protected exportOptions = {
    clearUserOverrides: false,
    clearConditionalRules: false,
  };
  protected sectionExpanded: Record<ManagedFeatureFlagsSectionKey, boolean> = {
    importExport: false,
    userOverrides: false,
    conditionalRules: false,
    evaluationPreview: false,
  };
  protected filters = {
    search: '',
    app: 'all',
    status: 'all',
  };
  protected previewContext: PreviewContextForm = {
    app: 'team-admin',
    userId: '',
    teamId: '',
    companyId: '',
    partnerId: '',
    state: '',
  };
  protected editor: ManagedFeatureFlagEditor = this.createEmptyEditor();
  protected loadedFlag: ManagedFeatureFlag | null = null;
  private partnerSearchIndex: PartnerSearchResult[] = [];
  private partnerLabelsById = new Map<string, string>();
  private teamSearchIndex: TeamSearchResult[] = [];
  private teamLabelsById = new Map<string, string>();
  protected teamSearchFailed = false;
  private companySearchIndex: CompanySearchResult[] = [];
  private companyLabelsById = new Map<string, string>();
  private userEmailsById = new Map<string, string>();
  private userEmailLookupsInProgress = new Set<string>();
  private editorBaseline = '';

  constructor(
    private managedFeatureFlagsService: ManagedFeatureFlagsService,
    private sessionService: SessionService,
    private snackBar: MatSnackBar,
    private partnersService: PartnersService,
    private usersService: UsersService,
    @Optional() private route?: ActivatedRoute,
    @Optional() private router?: Router,
    @Optional() private dialog?: MatDialog,
  ) {}

  async ngOnInit(): Promise<void> {
    this.previewContext.userId = this.sessionService.getUserId();
    await Promise.all([this.loadPartners(), this.loadCompanies(), this.loadTeams()]);

    const routeId = this.route?.snapshot.paramMap.get('id');
    if (routeId) {
      await this.loadFlagById(routeId);
      return;
    }

    if (this.route) {
      this.startCreate();
      this.sectionExpanded.importExport = this.route.snapshot.queryParamMap.get('transfer') === 'import';
      return;
    }

    // Retain the legacy initialization path for focused unit tests that
    // construct the component without Angular routing.
    await this.loadFlags();
  }

  protected async loadFlagById(id: string): Promise<void> {
    this.loading = true;
    this.loadFailed = false;
    try {
      const flag = await this.managedFeatureFlagsService.getById(id);
      this.selectFlag(flag);
    } catch (error) {
      console.error('Failed to load managed feature flag', error);
      this.loadFailed = true;
      this.snackBar.open('Unable to load this managed feature flag.', 'Close', { duration: 3000 });
    } finally {
      this.loading = false;
    }
  }

  protected async loadFlags(selectKey?: string): Promise<void> {
    this.loading = true;
    try {
      this.flags = await this.managedFeatureFlagsService.list();
      this.applyFilters();

      const nextSelectedFlag =
        this.flags.find((flag) => flag.key === selectKey)
        || this.flags.find((flag) => flag.id === this.selectedFlagId)
        || this.filteredFlags[0];

      if (nextSelectedFlag) {
        this.selectFlag(nextSelectedFlag);
      } else {
        this.startCreate();
      }
    } catch (error) {
      console.error('Failed to load managed feature flags', error);
      this.snackBar.open('Unable to load managed feature flags.', 'Close', { duration: 3000 });
    } finally {
      this.loading = false;
    }
  }

  protected applyFilters(): void {
    const search = this.filters.search.trim().toLowerCase();
    this.filteredFlags = this.flags.filter((flag) => {
      const matchesSearch = !search
        || flag.key.toLowerCase().includes(search)
        || flag.name.toLowerCase().includes(search)
        || (flag.description || '').toLowerCase().includes(search);
      const matchesApp = this.filters.app === 'all' || flag.appTargets.includes(this.filters.app as ManagedFeatureFlagApp);
      const matchesStatus =
        this.filters.status === 'all'
        || (this.filters.status === 'active' && flag.isActive)
        || (this.filters.status === 'inactive' && !flag.isActive);

      return matchesSearch && matchesApp && matchesStatus;
    });
  }

  protected selectFlag(flag: ManagedFeatureFlag): void {
    this.selectedFlagId = flag.id;
    this.loadedFlag = flag;
    this.editor = this.toEditor(flag);
    this.hydrateUserOverrideEmails([
      ...this.editor.includeUserIds,
      ...this.editor.excludeUserIds,
    ]);
    this.previewContext.app = flag.appTargets[0] || 'team-admin';
    this.previewResult = null;
    this.resetUserOverrideSelectionState();
    this.exportBlob = '';
    this.captureEditorBaseline();
  }

  protected startCreate(): void {
    this.selectedFlagId = '';
    this.loadedFlag = null;
    this.editor = this.createEmptyEditor();
    this.previewResult = null;
    this.resetUserOverrideSelectionState();
    this.exportBlob = '';
    this.captureEditorBaseline();
  }

  protected get hasUnsavedChanges(): boolean {
    return this.editorStateFingerprint() !== this.editorBaseline;
  }

  protected goBackToList(): void {
    if (!this.router) {
      return;
    }

    void this.router.navigate(['/superadmin/managed-feature-flags'], {
      queryParams: { transfer: null },
      queryParamsHandling: 'merge',
    });
  }

  public canDeactivate(): Promise<boolean> | boolean {
    if (!this.hasUnsavedChanges) {
      return true;
    }

    const message = 'You have unsaved feature flag changes. Leave this page and discard them?';
    if (!this.dialog) {
      return window.confirm(message);
    }

    return this.dialog.open(ConfirmDialogComponent, {
      data: new ConfirmDialogModel(
        'Discard unsaved changes?',
        message,
        false,
        'Discard Changes',
        'Keep Editing',
      ),
    }).afterClosed().toPromise().then((result) => result === true);
  }

  protected toggleSection(section: ManagedFeatureFlagsSectionKey): void {
    this.sectionExpanded[section] = !this.sectionExpanded[section];
  }

  protected addRule(): void {
    this.editor.rules.push(this.createEmptyRule());
  }

  protected removeRule(ruleId: string): void {
    this.editor.rules = this.editor.rules.filter((rule) => rule.id !== ruleId);
  }

  protected get saveDisabled(): boolean {
    return this.saving || this.deleting || !this.hasUnsavedChanges || !!this.editorValidationMessage;
  }

  protected get editorValidationMessage(): string | null {
    return this.getEditorValidationMessage();
  }

  protected getRuleValidationMessage(rule: ManagedFeatureFlagRuleEditor, index: number): string | null {
    return this.getRulePayloadValidationMessage(this.toRulePayload(rule), `Rule ${index + 1}`);
  }

  protected async saveFlag(): Promise<void> {
    const validationMessage = this.editorValidationMessage;
    if (validationMessage) {
      this.snackBar.open(validationMessage, 'Close', { duration: 4000 });
      return;
    }

    this.saving = true;
    try {
      const payload = this.buildPayload();
      const creating = !this.editor.id;
      const saved = this.editor.id
        ? await this.managedFeatureFlagsService.update(this.editor.id, payload)
        : await this.managedFeatureFlagsService.create(payload);
      this.snackBar.open(`Managed feature flag ${saved.key} saved.`, 'Close', { duration: 2500 });
      this.selectFlag(saved);

      if (creating && this.router) {
        await this.router.navigate(['/superadmin/managed-feature-flags', saved.id], {
          replaceUrl: true,
          queryParamsHandling: 'preserve',
        });
      }
    } catch (error: any) {
      console.error('Failed to save managed feature flag', error);
      this.snackBar.open(error?.data?.message || error?.message || 'Unable to save managed feature flag.', 'Close', {
        duration: 4000,
      });
    } finally {
      this.saving = false;
    }
  }

  protected async deleteFlag(): Promise<void> {
    if (!this.editor.id || this.deleting) {
      return;
    }

    const flagKey = this.editor.key;
    const confirmed = await this.confirmDelete(flagKey);
    if (!confirmed) {
      return;
    }

    this.deleting = true;
    try {
      await this.managedFeatureFlagsService.delete(this.editor.id);
      this.snackBar.open(`Managed feature flag ${this.editor.key} deleted.`, 'Close', { duration: 2500 });
      this.startCreate();
      this.goBackToList();
    } catch (error: any) {
      console.error('Failed to delete managed feature flag', error);
      this.snackBar.open(error?.data?.message || error?.message || 'Unable to delete managed feature flag.', 'Close', {
        duration: 4000,
      });
    } finally {
      this.deleting = false;
    }
  }

  private async confirmDelete(flagKey: string): Promise<boolean> {
    const message = `Permanently delete ${this.escapeHtml(flagKey)} from ${this.escapeHtml(this.connectedEnvironment)} (${this.escapeHtml(this.connectedBackend)})? This cannot be undone.`;
    if (!this.dialog) {
      return window.confirm(message);
    }

    const result = await this.dialog.open(ConfirmDialogComponent, {
      data: new ConfirmDialogModel(
        'Delete managed feature flag?',
        message,
        false,
        'Delete Flag',
        'Cancel',
      ),
    }).afterClosed().toPromise();
    return result === true;
  }

  private escapeHtml(value: string): string {
    return value.replace(/[&<>'"]/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;',
    }[character] || character));
  }

  protected previewDraft(): void {
    const previewKey = this.editor.key.trim().toUpperCase();
    if (!previewKey) {
      this.snackBar.open('Enter a flag key before running the preview.', 'Close', { duration: 2500 });
      return;
    }

    this.previewResult = null;
    try {
      this.previewResult = this.evaluatePreviewFlag(
        this.buildPreviewFlagRecord(),
        this.previewContext.app,
        this.buildDraftPreviewEvaluationContext(),
      );
      this.previewMode = 'draft';
    } catch (error: any) {
      console.error('Failed to preview managed feature flag draft', error);
      this.snackBar.open(error?.message || 'Unable to evaluate the current editor draft.', 'Close', {
        duration: 4000,
      });
    }
  }

  protected async previewSaved(): Promise<void> {
    const previewKey = this.editor.key.trim().toUpperCase();
    if (!previewKey) {
      this.snackBar.open('Enter a flag key before running the preview.', 'Close', { duration: 2500 });
      return;
    }

    this.previewLoading = true;
    this.previewResult = null;
    try {
      const [result] = await this.managedFeatureFlagsService.evaluate({
        app: this.previewContext.app,
        keys: [previewKey],
        context: this.buildSavedPreviewEvaluationContext(),
      }, true);
      this.previewResult = result || null;
      this.previewMode = 'saved';
    } catch (error: any) {
      console.error('Failed to preview saved managed feature flag', error);
      this.snackBar.open(error?.data?.message || error?.message || 'Unable to evaluate the saved feature flag.', 'Close', {
        duration: 4000,
      });
    } finally {
      this.previewLoading = false;
    }
  }

  protected async searchUsers(): Promise<void> {
    const query = this.userSearchQuery.trim();
    const requestId = ++this.userSearchRequestId;
    if (query.length < 2) {
      this.userSearchResults = [];
      this.userSearchLoading = false;
      return;
    }

    this.userSearchLoading = true;
    try {
      const results: UserSearchResult[] = (await this.usersService.queryUsers(query)).slice(0, 10);
      if (requestId !== this.userSearchRequestId || this.userSearchQuery.trim() !== query) {
        return;
      }

      this.userSearchResults = results;
      results.forEach((user) => this.rememberUserEmail(user));
    } catch (error) {
      if (requestId !== this.userSearchRequestId) {
        return;
      }

      console.error('Failed to search users for managed feature flags', error);
      this.userSearchResults = [];
    } finally {
      if (requestId === this.userSearchRequestId) {
        this.userSearchLoading = false;
      }
    }
  }

  protected selectSearchedUser(user: UserSearchResult): void {
    if (!user?.id) {
      return;
    }

    this.selectedUserOverrideId = user.id;
    this.selectedUserOverrideLabel = this.describeUser(user);
    this.rememberUserEmail(user);
  }

  protected selectManualUserOverride(): void {
    const userId = this.manualUserOverrideId.trim();
    if (!userId) {
      return;
    }

    this.selectedUserOverrideId = userId;
    this.selectedUserOverrideLabel = 'Manual user ID';
    this.manualUserOverrideId = '';
  }

  protected addPendingUserOverride(target: 'include' | 'exclude'): void {
    if (!this.selectedUserOverrideId) {
      return;
    }

    this.addUserOverride(this.selectedUserOverrideId, target);
    this.hydrateUserOverrideEmails([this.selectedUserOverrideId]);
    this.clearPendingUserOverrideSelection();
  }

  protected clearPendingUserOverrideSelection(): void {
    this.selectedUserOverrideId = '';
    this.selectedUserOverrideLabel = '';
  }

  protected removeIncludedUser(userId: string): void {
    this.editor.includeUserIds = this.editor.includeUserIds.filter((id) => id !== userId);
  }

  protected removeExcludedUser(userId: string): void {
    this.editor.excludeUserIds = this.editor.excludeUserIds.filter((id) => id !== userId);
  }

  protected dropUserOverride(event: CdkDragDrop<string[]>): void {
    const isIncludeDropTarget = event.container.id === 'includeUserIdsDropList';

    if (event.previousContainer === event.container) {
      const reordered = [...event.container.data];
      moveItemInArray(reordered, event.previousIndex, event.currentIndex);

      if (isIncludeDropTarget) {
        this.editor.includeUserIds = reordered;
      } else {
        this.editor.excludeUserIds = reordered;
      }
      return;
    }

    const sourceValues = [...event.previousContainer.data];
    const [movedUserId] = sourceValues.splice(event.previousIndex, 1);
    if (!movedUserId) {
      return;
    }

    const targetValues = event.container.data.filter((userId) => userId !== movedUserId);
    const nextIndex = Math.min(event.currentIndex, targetValues.length);
    targetValues.splice(nextIndex, 0, movedUserId);

    if (isIncludeDropTarget) {
      this.editor.includeUserIds = targetValues;
      this.editor.excludeUserIds = sourceValues.filter((userId) => userId !== movedUserId);
    } else {
      this.editor.excludeUserIds = targetValues;
      this.editor.includeUserIds = sourceValues.filter((userId) => userId !== movedUserId);
    }
  }

  protected describeUser(user: UserSearchResult): string {
    const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    return name ? `${name} (${user.email || user.id})` : user.email || user.id;
  }

  protected describeUserOverrideById(userId: string): string {
    const email = this.userEmailsById.get(userId);
    return email ? `${email} (${userId})` : userId;
  }

  protected searchRuleCompanies(rule: ManagedFeatureFlagRuleEditor): void {
    const query = rule.companySearchQuery.trim().toLowerCase();
    rule.companySearchResults = query
      ? this.companySearchIndex
        .filter((company) => company.searchText.includes(query))
        .slice(0, this.partnerSearchResultsLimit)
      : [];
  }

  protected selectSearchedCompany(rule: ManagedFeatureFlagRuleEditor, company: CompanySearchResult): void {
    if (!company?.id) {
      return;
    }
    rule.selectedCompanyId = company.id;
    rule.selectedCompanyLabel = company.name;
  }

  protected selectManualRuleCompany(rule: ManagedFeatureFlagRuleEditor): void {
    const companyId = rule.manualCompanyId.trim();
    if (!companyId) {
      return;
    }
    rule.selectedCompanyId = companyId;
    rule.selectedCompanyLabel = this.companyLabelsById.get(companyId) || 'Manual company ID';
    rule.manualCompanyId = '';
  }

  protected addPendingRuleCompany(rule: ManagedFeatureFlagRuleEditor, target: 'include' | 'exclude'): void {
    if (!rule.selectedCompanyId) {
      return;
    }
    this.addRuleCompany(rule, rule.selectedCompanyId, target);
    this.clearPendingRuleCompanySelection(rule);
  }

  protected clearPendingRuleCompanySelection(rule: ManagedFeatureFlagRuleEditor): void {
    rule.selectedCompanyId = '';
    rule.selectedCompanyLabel = '';
  }

  protected removeIncludedCompany(rule: ManagedFeatureFlagRuleEditor, companyId: string): void {
    rule.companyIds = rule.companyIds.filter((id) => id !== companyId);
  }

  protected removeExcludedCompany(rule: ManagedFeatureFlagRuleEditor, companyId: string): void {
    rule.excludeCompanyIds = rule.excludeCompanyIds.filter((id) => id !== companyId);
  }

  protected trackByCompanyId(_: number, companyId: string): string {
    return companyId;
  }

  protected trackByCompanyResultId(_: number, company: CompanySearchResult): string {
    return company.id;
  }

  protected describeCompanyById(companyId: string): string {
    const label = this.companyLabelsById.get(companyId);
    return label ? `${label} (${companyId})` : companyId;
  }

  protected searchRuleTeams(rule: ManagedFeatureFlagRuleEditor): void {
    const query = rule.teamSearchQuery.trim().toLowerCase();
    rule.teamSearchResults = query
      ? this.teamSearchIndex
        .filter((team) => team.searchText.includes(query))
        .slice(0, this.partnerSearchResultsLimit)
      : [];
  }

  protected selectSearchedTeam(rule: ManagedFeatureFlagRuleEditor, team: TeamSearchResult): void {
    if (!team?.id) {
      return;
    }
    rule.selectedTeamId = team.id;
    rule.selectedTeamLabel = team.name;
  }

  protected selectManualRuleTeam(rule: ManagedFeatureFlagRuleEditor): void {
    const teamId = rule.manualTeamId.trim();
    if (!teamId) {
      return;
    }
    rule.selectedTeamId = teamId;
    rule.selectedTeamLabel = this.teamLabelsById.get(teamId) || 'Manual team ID';
    rule.manualTeamId = '';
  }

  protected addPendingRuleTeam(rule: ManagedFeatureFlagRuleEditor, target: 'include' | 'exclude'): void {
    if (!rule.selectedTeamId) {
      return;
    }
    this.addRuleTeam(rule, rule.selectedTeamId, target);
    this.clearPendingRuleTeamSelection(rule);
  }

  protected clearPendingRuleTeamSelection(rule: ManagedFeatureFlagRuleEditor): void {
    rule.selectedTeamId = '';
    rule.selectedTeamLabel = '';
  }

  protected removeIncludedTeam(rule: ManagedFeatureFlagRuleEditor, teamId: string): void {
    rule.teamIds = rule.teamIds.filter((id) => id !== teamId);
  }

  protected removeExcludedTeam(rule: ManagedFeatureFlagRuleEditor, teamId: string): void {
    rule.excludeTeamIds = rule.excludeTeamIds.filter((id) => id !== teamId);
  }

  protected trackByTeamId(_: number, teamId: string): string {
    return teamId;
  }

  protected trackByTeamResultId(_: number, team: TeamSearchResult): string {
    return team.id;
  }

  protected describeTeamById(teamId: string): string {
    const label = this.teamLabelsById.get(teamId);
    return label ? `${label} (${teamId})` : teamId;
  }

  protected searchRulePartners(rule: ManagedFeatureFlagRuleEditor): void {
    const query = rule.partnerSearchQuery.trim().toLowerCase();
    if (!query) {
      rule.partnerSearchResults = [];
      return;
    }

    rule.partnerSearchResults = this.partnerSearchIndex
      .filter((partner) => partner.searchText.includes(query))
      .slice(0, this.partnerSearchResultsLimit);
  }

  protected selectSearchedPartner(rule: ManagedFeatureFlagRuleEditor, partner: PartnerSearchResult): void {
    if (!partner?.id) {
      return;
    }

    rule.selectedPartnerId = partner.id;
    rule.selectedPartnerLabel = partner.name;
  }

  protected selectManualRulePartner(rule: ManagedFeatureFlagRuleEditor): void {
    const partnerId = rule.manualPartnerId.trim();
    if (!partnerId) {
      return;
    }

    rule.selectedPartnerId = partnerId;
    rule.selectedPartnerLabel = this.partnerLabelsById.get(partnerId) || 'Manual partner ID';
    rule.manualPartnerId = '';
  }

  protected addPendingRulePartner(rule: ManagedFeatureFlagRuleEditor, target: 'include' | 'exclude'): void {
    if (!rule.selectedPartnerId) {
      return;
    }

    this.addRulePartner(rule, rule.selectedPartnerId, target);
    this.clearPendingRulePartnerSelection(rule);
  }

  protected clearPendingRulePartnerSelection(rule: ManagedFeatureFlagRuleEditor): void {
    rule.selectedPartnerId = '';
    rule.selectedPartnerLabel = '';
  }

  protected removeIncludedPartner(rule: ManagedFeatureFlagRuleEditor, partnerId: string): void {
    rule.partnerIds = rule.partnerIds.filter((id) => id !== partnerId);
  }

  protected removeExcludedPartner(rule: ManagedFeatureFlagRuleEditor, partnerId: string): void {
    rule.excludePartnerIds = rule.excludePartnerIds.filter((id) => id !== partnerId);
  }

  protected trackByPartnerId(_: number, partnerId: string): string {
    return partnerId;
  }

  protected trackByPartnerResultId(_: number, partner: PartnerSearchResult): string {
    return partner.id;
  }

  protected describePartnerById(partnerId: string): string {
    const label = this.partnerLabelsById.get(partnerId);
    return label ? `${label} (${partnerId})` : partnerId;
  }

  protected trackByFlagId(_: number, flag: ManagedFeatureFlag): string {
    return flag.id;
  }

  protected trackByRuleId(_: number, rule: ManagedFeatureFlagRuleEditor): string {
    return rule.id;
  }

  protected trackByUserId(_: number, userId: string): string {
    return userId;
  }

  protected refreshExportBlob(): void {
    if (!this.exportBlob) {
      return;
    }

    try {
      this.exportBlob = this.buildExportBlob();
    } catch {
      this.exportBlob = '';
    }
  }

  protected generateExportBlob(): void {
    try {
      this.exportBlob = this.buildExportBlob();
      this.snackBar.open('Feature flag export generated.', 'Close', { duration: 2500 });
    } catch (error: any) {
      this.snackBar.open(error?.message || 'Unable to generate feature flag export.', 'Close', {
        duration: 4000,
      });
    }
  }

  protected async copyExportBlob(): Promise<void> {
    try {
      const blob = this.buildExportBlob();
      this.exportBlob = blob;

      if (!window?.navigator?.clipboard?.writeText) {
        throw new Error('Clipboard copy is not available in this browser.');
      }

      await window.navigator.clipboard.writeText(blob);
      this.snackBar.open('Feature flag export copied to clipboard.', 'Close', { duration: 2500 });
    } catch (error: any) {
      this.snackBar.open(
        error?.message || 'Unable to copy the export. You can still copy it from the export box below.',
        'Close',
        { duration: 4000 },
      );
    }
  }

  protected clearImportBlob(): void {
    this.importBlob = '';
  }

  protected loadImportBlob(): void {
    try {
      const payload = this.parseImportedPayload(this.importBlob);
      this.selectedFlagId = '';
      this.editor = this.toEditorFromPayload(payload);
      this.hydrateUserOverrideEmails([
        ...this.editor.includeUserIds,
        ...this.editor.excludeUserIds,
      ]);
      this.previewContext.app = payload.appTargets[0] || 'team-admin';
      this.previewResult = null;
      this.resetUserOverrideSelectionState();
      this.exportBlob = '';
      this.snackBar.open('Imported feature flag loaded into the editor. Save to create it in this environment.', 'Close', {
        duration: 3500,
      });
    } catch (error: any) {
      this.snackBar.open(error?.message || 'Unable to import feature flag blob.', 'Close', {
        duration: 4500,
      });
    }
  }

  private addUserOverride(userId: string, target: 'include' | 'exclude'): void {
    if (target === 'include') {
      this.editor.includeUserIds = this.uniqueValues([...this.editor.includeUserIds, userId]);
      this.editor.excludeUserIds = this.editor.excludeUserIds.filter((id) => id !== userId);
    } else {
      this.editor.excludeUserIds = this.uniqueValues([...this.editor.excludeUserIds, userId]);
      this.editor.includeUserIds = this.editor.includeUserIds.filter((id) => id !== userId);
    }
  }

  private hydrateUserOverrideEmails(userIds: string[]): void {
    this.uniqueValues(userIds).forEach((userId) => {
      if (this.userEmailsById.has(userId) || this.userEmailLookupsInProgress.has(userId)) {
        return;
      }

      this.userEmailLookupsInProgress.add(userId);
      this.usersService.queryUsers(userId, 0, 10)
        .then((users: UserSearchResult[]) => {
          const matchingUser = users.find((user) => user.id === userId);
          if (matchingUser) {
            this.rememberUserEmail(matchingUser);
          }
        })
        .catch((error) => {
          console.error(`Failed to load the email for managed feature flag user override ${userId}`, error);
        })
        .finally(() => {
          this.userEmailLookupsInProgress.delete(userId);
        });
    });
  }

  private rememberUserEmail(user: UserSearchResult): void {
    const userId = user?.id?.trim();
    const email = user?.email?.trim();
    if (userId && email) {
      this.userEmailsById.set(userId, email);
    }
  }

  private buildPayload(): ManagedFeatureFlagPayload {
    const validationMessage = this.getEditorValidationMessage();
    if (validationMessage) {
      throw new Error(validationMessage);
    }

    const key = this.editor.key.trim().toUpperCase();
    const name = this.editor.name.trim();

    return {
      key,
      name,
      description: this.editor.description.trim() || null,
      appTargets: this.uniqueValues(this.editor.appTargets) as ManagedFeatureFlagApp[],
      isActive: this.editor.isActive,
      defaultEnabled: this.editor.defaultEnabled,
      userOverrides: {
        includeUserIds: this.uniqueValues(this.editor.includeUserIds),
        excludeUserIds: this.uniqueValues(this.editor.excludeUserIds),
      },
      rules: this.editor.rules.map((rule) => this.toRulePayload(rule)),
      schemaVersion: DEFAULT_SCHEMA_VERSION,
    };
  }

  private buildPreviewFlagRecord(): PreviewFlagRecord {
    const key = this.editor.key.trim().toUpperCase();
    if (!key) {
      throw new Error('Key is required.');
    }

    const invalidRule = this.editor.rules
      .map((rule, index) => this.getRuleValidationMessage(rule, index))
      .find((message) => !!message);
    if (invalidRule) {
      throw new Error(invalidRule);
    }

    return {
      key,
      appTargets: this.uniqueValues(this.editor.appTargets) as ManagedFeatureFlagApp[],
      isActive: this.editor.isActive,
      defaultEnabled: this.editor.defaultEnabled,
      userOverrides: {
        includeUserIds: this.uniqueValues(this.editor.includeUserIds),
        excludeUserIds: this.uniqueValues(this.editor.excludeUserIds),
      },
      rules: this.editor.rules.map((rule) => this.toRulePayload(rule)),
    };
  }

  private toEditorFromPayload(payload: ManagedFeatureFlagPayload): ManagedFeatureFlagEditor {
    return {
      key: payload.key,
      name: payload.name,
      description: payload.description || '',
      appTargets: [...payload.appTargets],
      isActive: !!payload.isActive,
      defaultEnabled: !!payload.defaultEnabled,
      includeUserIds: [...(payload.userOverrides?.includeUserIds || [])],
      excludeUserIds: [...(payload.userOverrides?.excludeUserIds || [])],
      rules: (payload.rules || []).map((rule) => ({
        id: rule.id,
        effect: rule.effect,
        teamIds: [...rule.teamIds],
        excludeTeamIds: [...rule.excludeTeamIds],
        companyIds: [...(rule.companyIds || [])],
        excludeCompanyIds: [...(rule.excludeCompanyIds || [])],
        partnerIds: [...rule.partnerIds],
        excludePartnerIds: [...rule.excludePartnerIds],
        statesText: rule.states.join('\n'),
        excludeStatesText: rule.excludeStates.join('\n'),
        partnerSearchQuery: '',
        partnerSearchResults: [],
        manualPartnerId: '',
        selectedPartnerId: '',
        selectedPartnerLabel: '',
        teamSearchQuery: '',
        teamSearchResults: [],
        manualTeamId: '',
        selectedTeamId: '',
        selectedTeamLabel: '',
        companySearchQuery: '',
        companySearchResults: [],
        manualCompanyId: '',
        selectedCompanyId: '',
        selectedCompanyLabel: '',
      })),
    };
  }

  private toRulePayload(rule: ManagedFeatureFlagRuleEditor): ManagedFeatureFlagRule {
    return {
      id: rule.id,
      effect: rule.effect,
      teamIds: this.uniqueValues(rule.teamIds),
      excludeTeamIds: this.uniqueValues(rule.excludeTeamIds),
      companyIds: this.uniqueValues(rule.companyIds),
      excludeCompanyIds: this.uniqueValues(rule.excludeCompanyIds),
      partnerIds: this.uniqueValues(rule.partnerIds),
      excludePartnerIds: this.uniqueValues(rule.excludePartnerIds),
      states: this.parseDelimitedValues(rule.statesText).map((value) => value.toUpperCase()),
      excludeStates: this.parseDelimitedValues(rule.excludeStatesText).map((value) => value.toUpperCase()),
    };
  }

  private toEditor(flag: ManagedFeatureFlag): ManagedFeatureFlagEditor {
    return {
      id: flag.id,
      key: flag.key,
      name: flag.name,
      description: flag.description || '',
      appTargets: [...flag.appTargets],
      isActive: !!flag.isActive,
      defaultEnabled: !!flag.defaultEnabled,
      includeUserIds: [...(flag.userOverrides?.includeUserIds || [])],
      excludeUserIds: [...(flag.userOverrides?.excludeUserIds || [])],
      rules: (flag.rules || []).map((rule) => ({
        id: rule.id,
        effect: rule.effect,
        teamIds: [...rule.teamIds],
        excludeTeamIds: [...rule.excludeTeamIds],
        companyIds: [...(rule.companyIds || [])],
        excludeCompanyIds: [...(rule.excludeCompanyIds || [])],
        partnerIds: [...rule.partnerIds],
        excludePartnerIds: [...rule.excludePartnerIds],
        statesText: rule.states.join('\n'),
        excludeStatesText: rule.excludeStates.join('\n'),
        partnerSearchQuery: '',
        partnerSearchResults: [],
        manualPartnerId: '',
        selectedPartnerId: '',
        selectedPartnerLabel: '',
        teamSearchQuery: '',
        teamSearchResults: [],
        manualTeamId: '',
        selectedTeamId: '',
        selectedTeamLabel: '',
        companySearchQuery: '',
        companySearchResults: [],
        manualCompanyId: '',
        selectedCompanyId: '',
        selectedCompanyLabel: '',
      })),
    };
  }

  private createEmptyEditor(): ManagedFeatureFlagEditor {
    return {
      key: '',
      name: '',
      description: '',
      appTargets: ['team-admin'],
      isActive: true,
      defaultEnabled: false,
      includeUserIds: [],
      excludeUserIds: [],
      rules: [],
    };
  }

  private createEmptyRule(): ManagedFeatureFlagRuleEditor {
    return {
      id: this.createRuleId(),
      effect: 'enable',
      teamIds: [],
      excludeTeamIds: [],
      companyIds: [],
      excludeCompanyIds: [],
      partnerIds: [],
      excludePartnerIds: [],
      statesText: '',
      excludeStatesText: '',
      partnerSearchQuery: '',
      partnerSearchResults: [],
      manualPartnerId: '',
      selectedPartnerId: '',
      selectedPartnerLabel: '',
      teamSearchQuery: '',
      teamSearchResults: [],
      manualTeamId: '',
      selectedTeamId: '',
      selectedTeamLabel: '',
      companySearchQuery: '',
      companySearchResults: [],
      manualCompanyId: '',
      selectedCompanyId: '',
      selectedCompanyLabel: '',
    };
  }

  private parseDelimitedValues(value: string): string[] {
    return this.uniqueValues(
      (value || '')
        .split(/[\n,]/)
        .map((item) => item.trim())
        .filter((item) => !!item),
    );
  }

  private uniqueValues(values: string[]): string[] {
    return Array.from(new Set(values.map((value) => value.trim()).filter((value) => !!value)));
  }

  private getEditorValidationMessage(): string | null {
    if (!this.editor.key.trim()) {
      return 'Key is required.';
    }

    if (!this.editor.name.trim()) {
      return 'Name is required.';
    }

    if (this.uniqueValues(this.editor.appTargets).length === 0) {
      return 'Select at least one app target.';
    }

    for (const [index, rule] of this.editor.rules.entries()) {
      const validationMessage = this.getRuleValidationMessage(rule, index);
      if (validationMessage) {
        return validationMessage;
      }
    }

    return null;
  }

  private getRulePayloadValidationMessage(rule: ManagedFeatureFlagRule, ruleLabel: string): string | null {
    if (this.previewRuleSpecificity(rule) === 0) {
      return `${ruleLabel} must include at least one team, company, partner, or state condition.`;
    }

    return null;
  }

  private async loadPartners(): Promise<void> {
    try {
      const partners = await this.partnersService.getAllPartners();
      const normalizedPartners = this.normalizePartnerSearchIndex(partners || []);
      this.partnerSearchIndex = normalizedPartners;
      this.partnerLabelsById = new Map(normalizedPartners.map((partner) => [partner.id, partner.name]));
    } catch (error) {
      console.error('Failed to load partners for managed feature flags', error);
      this.partnerSearchIndex = [];
      this.partnerLabelsById = new Map();
      this.snackBar.open('Unable to load partners for rule search.', 'Close', { duration: 3000 });
    }
  }

  private async loadCompanies(): Promise<void> {
    try {
      const companies = await this.managedFeatureFlagsService.listCompanyOptions();
      const seenIds = new Set<string>();
      this.companySearchIndex = (Array.isArray(companies) ? companies : [])
        .filter((company: ManagedFeatureFlagCompanyOption) => {
          const declaredType = `${company?.recordType || company?.type || ''}`.trim().toLowerCase();
          return !declaredType || declaredType === 'company' || declaredType === 'employer-company';
        })
        .map((company: ManagedFeatureFlagCompanyOption) => {
          const id = `${company?.id || ''}`.trim();
          const name = `${company?.name || id}`.trim();
          return id ? { id, name, searchText: `${id} ${name}`.toLowerCase() } : null;
        })
        .filter((company: CompanySearchResult | null): company is CompanySearchResult => {
          if (!company || seenIds.has(company.id)) {
            return false;
          }
          seenIds.add(company.id);
          return true;
        })
        .sort((left: CompanySearchResult, right: CompanySearchResult) => left.name.localeCompare(right.name));
      this.companyLabelsById = new Map(this.companySearchIndex.map((company) => [company.id, company.name]));
    } catch (error) {
      console.error('Failed to load companies for managed feature flags', error);
      this.companySearchIndex = [];
      this.companyLabelsById = new Map();
      this.snackBar.open('Unable to load companies for rule search.', 'Close', { duration: 3000 });
    }
  }

  private async loadTeams(): Promise<void> {
    this.teamSearchFailed = false;
    try {
      const teams = await this.managedFeatureFlagsService.listTeamOptions();
      const seenIds = new Set<string>();
      this.teamSearchIndex = (Array.isArray(teams) ? teams : [])
        .map((team: ManagedFeatureFlagTeamOption) => {
          const id = `${team?.id || ''}`.trim();
          const name = `${team?.name || id}`.trim();
          return id ? { id, name, searchText: `${id} ${name}`.toLowerCase() } : null;
        })
        .filter((team: TeamSearchResult | null): team is TeamSearchResult => {
          if (!team || seenIds.has(team.id)) {
            return false;
          }
          seenIds.add(team.id);
          return true;
        })
        .sort((left: TeamSearchResult, right: TeamSearchResult) => left.name.localeCompare(right.name));
      this.teamLabelsById = new Map(this.teamSearchIndex.map((team) => [team.id, team.name]));
    } catch (error) {
      this.teamSearchFailed = true;
      console.error('Failed to load teams for managed feature flags', error);
      this.teamSearchIndex = [];
      this.teamLabelsById = new Map();
      this.snackBar.open('Unable to load teams for rule search.', 'Close', { duration: 3000 });
    }
  }

  private normalizePartnerSearchIndex(partners: Partner[]): PartnerSearchResult[] {
    const seenIds = new Set<string>();

    return partners
      .map((partner) => this.toPartnerSearchResult(partner))
      .filter((partner): partner is PartnerSearchResult => {
        if (!partner || seenIds.has(partner.id)) {
          return false;
        }

        seenIds.add(partner.id);
        return true;
      })
      .sort((left, right) => left.name.localeCompare(right.name));
  }

  private toPartnerSearchResult(partner: Partner): PartnerSearchResult | null {
    const id = `${partner?.partnerId || ''}`.trim();
    if (!id) {
      return null;
    }

    const name = `${partner?.name || partner?.shortName || id}`.trim();
    const shortName = `${partner?.shortName || ''}`.trim();
    const searchParts = [id, name, shortName].filter((value) => !!value);
    return {
      id,
      name: shortName && shortName !== name ? `${name} (${shortName})` : name,
      searchText: searchParts.join(' ').toLowerCase(),
    };
  }

  private addRulePartner(rule: ManagedFeatureFlagRuleEditor, partnerId: string, target: 'include' | 'exclude'): void {
    if (target === 'include') {
      rule.partnerIds = this.uniqueValues([...rule.partnerIds, partnerId]);
      rule.excludePartnerIds = rule.excludePartnerIds.filter((id) => id !== partnerId);
      return;
    }

    rule.excludePartnerIds = this.uniqueValues([...rule.excludePartnerIds, partnerId]);
    rule.partnerIds = rule.partnerIds.filter((id) => id !== partnerId);
  }

  private addRuleCompany(rule: ManagedFeatureFlagRuleEditor, companyId: string, target: 'include' | 'exclude'): void {
    if (target === 'include') {
      rule.companyIds = this.uniqueValues([...rule.companyIds, companyId]);
      rule.excludeCompanyIds = rule.excludeCompanyIds.filter((id) => id !== companyId);
      return;
    }
    rule.excludeCompanyIds = this.uniqueValues([...rule.excludeCompanyIds, companyId]);
    rule.companyIds = rule.companyIds.filter((id) => id !== companyId);
  }

  private addRuleTeam(rule: ManagedFeatureFlagRuleEditor, teamId: string, target: 'include' | 'exclude'): void {
    if (target === 'include') {
      rule.teamIds = this.uniqueValues([...rule.teamIds, teamId]);
      rule.excludeTeamIds = rule.excludeTeamIds.filter((id) => id !== teamId);
      return;
    }
    rule.excludeTeamIds = this.uniqueValues([...rule.excludeTeamIds, teamId]);
    rule.teamIds = rule.teamIds.filter((id) => id !== teamId);
  }

  private buildDraftPreviewEvaluationContext(): Required<PreviewContextForm> {
    return {
      app: this.previewContext.app,
      userId: this.previewContext.userId.trim(),
      teamId: this.previewContext.teamId.trim(),
      companyId: this.previewContext.companyId.trim(),
      partnerId: this.previewContext.partnerId.trim(),
      state: this.previewContext.state.trim().toUpperCase(),
    };
  }

  private buildSavedPreviewEvaluationContext(): {
    userId?: string;
    teamId?: string;
    companyId?: string;
    partnerId?: string;
    state?: string;
  } {
    return {
      userId: this.previewContext.userId.trim() || undefined,
      teamId: this.previewContext.teamId.trim() || undefined,
      companyId: this.previewContext.companyId.trim() || undefined,
      partnerId: this.previewContext.partnerId.trim() || undefined,
      state: this.previewContext.state.trim().toUpperCase() || undefined,
    };
  }

  private evaluatePreviewFlag(
    flag: PreviewFlagRecord,
    app: ManagedFeatureFlagApp,
    context: Required<PreviewContextForm>,
  ): ManagedFeatureFlagEvaluationResult {
    if (!flag.appTargets.includes(app)) {
      return {
        key: flag.key,
        enabled: false,
        source: 'app-target-mismatch',
      };
    }

    if (!flag.isActive) {
      return {
        key: flag.key,
        enabled: false,
        source: 'inactive',
      };
    }

    if (context.userId && flag.userOverrides.excludeUserIds.includes(context.userId)) {
      return {
        key: flag.key,
        enabled: false,
        source: 'user-exclude',
      };
    }

    if (context.userId && flag.userOverrides.includeUserIds.includes(context.userId)) {
      return {
        key: flag.key,
        enabled: true,
        source: 'user-include',
      };
    }

    const previewContext = {
      userId: context.userId || undefined,
      teamId: context.teamId || undefined,
      companyId: context.companyId || undefined,
      partnerId: context.partnerId || undefined,
      state: context.state || undefined,
    };

    const matchedRules = flag.rules.filter((rule) => this.previewRuleMatches(rule, previewContext));
    if (matchedRules.length > 0) {
      const bestRule = [...matchedRules].sort((left, right) => {
        const specificityDiff = this.previewRuleSpecificity(right) - this.previewRuleSpecificity(left);
        if (specificityDiff !== 0) {
          return specificityDiff;
        }
        if (left.effect === right.effect) {
          return left.id.localeCompare(right.id);
        }
        return left.effect === 'disable' ? -1 : 1;
      })[0];

      return {
        key: flag.key,
        enabled: bestRule.effect === 'enable',
        source: bestRule.effect === 'enable' ? 'rule-enable' : 'rule-disable',
        matchedRuleId: bestRule.id,
      };
    }

    return {
      key: flag.key,
      enabled: flag.defaultEnabled,
      source: flag.defaultEnabled ? 'default-enabled' : 'default-disabled',
    };
  }

  private previewRuleMatches(
    rule: ManagedFeatureFlagRule,
    context: {
      userId?: string;
      teamId?: string;
      companyId?: string;
      partnerId?: string;
      state?: string;
    },
  ): boolean {
    return (
      this.previewMatchScalarDimension(context.teamId, rule.teamIds, rule.excludeTeamIds)
      && this.previewMatchScalarDimension(context.companyId, rule.companyIds, rule.excludeCompanyIds)
      && this.previewMatchScalarDimension(context.partnerId, rule.partnerIds, rule.excludePartnerIds)
      && this.previewMatchScalarDimension(context.state, rule.states, rule.excludeStates)
    );
  }

  private previewMatchScalarDimension(
    value: string | undefined,
    includes: string[],
    excludes: string[],
  ): boolean {
    if (includes.length === 0 && excludes.length === 0) {
      return true;
    }

    if (!value) {
      return false;
    }

    if (includes.length > 0 && !includes.includes(value)) {
      return false;
    }

    if (excludes.length > 0 && excludes.includes(value)) {
      return false;
    }

    return true;
  }

  private previewRuleSpecificity(rule: ManagedFeatureFlagRule): number {
    return [
      rule.teamIds,
      rule.excludeTeamIds,
      rule.companyIds,
      rule.excludeCompanyIds,
      rule.partnerIds,
      rule.excludePartnerIds,
      rule.states,
      rule.excludeStates,
    ].filter((value) => value.length > 0).length;
  }

  private buildExportBlob(): string {
    const document = this.buildExportDocument();
    return JSON.stringify(document, null, 2);
  }

  private buildExportDocument(): ManagedFeatureFlagTransferDocument {
    const payload = this.buildPayload();
    return {
      format: 'managed-feature-flag-transfer',
      version: 1,
      source: {
        backend: this.connectedBackend,
        environment: this.connectedEnvironment,
        exportedAt: new Date().toISOString(),
        clearUserOverrides: this.exportOptions.clearUserOverrides,
        clearConditionalRules: this.exportOptions.clearConditionalRules,
      },
      flag: {
        ...payload,
        userOverrides: this.exportOptions.clearUserOverrides
          ? {
            includeUserIds: [],
            excludeUserIds: [],
          }
          : payload.userOverrides,
        rules: this.exportOptions.clearConditionalRules ? [] : payload.rules,
      },
    };
  }

  private parseImportedPayload(rawValue: string): ManagedFeatureFlagPayload {
    const trimmed = rawValue.trim();
    if (!trimmed) {
      throw new Error('Paste a feature flag export blob first.');
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      throw new Error('The feature flag import blob must be valid JSON.');
    }

    const candidate = this.extractImportedPayloadCandidate(parsed);
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
      throw new Error('The feature flag import blob does not contain a valid flag payload.');
    }

    const record = candidate as Record<string, unknown>;
    const key = this.requireImportedString(record.key, 'key').toUpperCase();
    const name = this.requireImportedString(record.name, 'name');
    const description = this.normalizeImportedDescription(record.description);

    const appTargets = this.normalizeImportedAppTargets(record.appTargets);
    const isActive = this.requireImportedBoolean(record.isActive, 'isActive');
    const defaultEnabled = this.requireImportedBoolean(record.defaultEnabled, 'defaultEnabled');
    const userOverrides = this.normalizeImportedUserOverrides(record.userOverrides);
    const rules = this.normalizeImportedRules(record.rules);
    const schemaVersion = this.normalizeImportedSchemaVersion(record.schemaVersion);

    return {
      key,
      name,
      description,
      appTargets,
      isActive,
      defaultEnabled,
      userOverrides,
      rules,
      schemaVersion,
    };
  }

  private extractImportedPayloadCandidate(parsed: unknown): unknown {
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }

    const record = parsed as Record<string, unknown>;
    if (record.format === 'managed-feature-flag-transfer' && record.flag) {
      return record.flag;
    }

    return parsed;
  }

  private requireImportedString(value: unknown, fieldName: string): string {
    if (typeof value !== 'string' || !value.trim()) {
      throw new Error(`Imported feature flag ${fieldName} is required.`);
    }

    return value.trim();
  }

  private requireImportedBoolean(value: unknown, fieldName: string): boolean {
    if (typeof value !== 'boolean') {
      throw new Error(`Imported feature flag ${fieldName} must be a boolean.`);
    }

    return value;
  }

  private normalizeImportedDescription(value: unknown): string | null {
    if (value === undefined || value === null || value === '') {
      return null;
    }

    if (typeof value !== 'string') {
      throw new Error('Imported feature flag description must be a string.');
    }

    return value.trim() || null;
  }

  private normalizeImportedAppTargets(value: unknown): ManagedFeatureFlagApp[] {
    if (!Array.isArray(value) || value.length === 0) {
      throw new Error('Imported feature flag appTargets must be a non-empty array.');
    }

    const appTargets = value.map((item) => {
      if (item !== 'customer-app' && item !== 'team-admin' && item !== 'backend') {
        throw new Error('Imported feature flag appTargets contain an unsupported app.');
      }

      return item;
    });

    return this.uniqueValues(appTargets) as ManagedFeatureFlagApp[];
  }

  private normalizeImportedUserOverrides(value: unknown): ManagedFeatureFlagPayload['userOverrides'] {
    if (value === undefined || value === null) {
      return {
        includeUserIds: [],
        excludeUserIds: [],
      };
    }

    if (typeof value !== 'object' || Array.isArray(value)) {
      throw new Error('Imported feature flag userOverrides must be an object.');
    }

    const record = value as Record<string, unknown>;
    return {
      includeUserIds: this.normalizeImportedStringArray(record.includeUserIds, 'includeUserIds'),
      excludeUserIds: this.normalizeImportedStringArray(record.excludeUserIds, 'excludeUserIds'),
    };
  }

  private normalizeImportedRules(value: unknown): ManagedFeatureFlagRule[] {
    if (value === undefined || value === null) {
      return [];
    }

    if (!Array.isArray(value)) {
      throw new Error('Imported feature flag rules must be an array.');
    }

    return value.map((item, index) => {
      if (!item || typeof item !== 'object' || Array.isArray(item)) {
        throw new Error(`Imported feature flag rule ${index + 1} must be an object.`);
      }

      const record = item as Record<string, unknown>;
      const effect = record.effect;
      if (effect !== 'enable' && effect !== 'disable') {
        throw new Error(`Imported feature flag rule ${index + 1} must use enable or disable.`);
      }

      const normalizedRule: ManagedFeatureFlagRule = {
        id: typeof record.id === 'string' && record.id.trim() ? record.id.trim() : this.createRuleId(),
        effect,
        teamIds: this.normalizeImportedStringArray(record.teamIds, 'teamIds'),
        excludeTeamIds: this.normalizeImportedStringArray(record.excludeTeamIds, 'excludeTeamIds'),
        companyIds: this.normalizeImportedStringArray(record.companyIds, 'companyIds'),
        excludeCompanyIds: this.normalizeImportedStringArray(record.excludeCompanyIds, 'excludeCompanyIds'),
        partnerIds: this.normalizeImportedStringArray(record.partnerIds, 'partnerIds'),
        excludePartnerIds: this.normalizeImportedStringArray(record.excludePartnerIds, 'excludePartnerIds'),
        states: this.normalizeImportedStringArray(record.states, 'states').map((state) => state.toUpperCase()),
        excludeStates: this.normalizeImportedStringArray(record.excludeStates, 'excludeStates').map((state) => state.toUpperCase()),
      };

      const validationMessage = this.getRulePayloadValidationMessage(
        normalizedRule,
        `Imported feature flag rule ${index + 1}`,
      );
      if (validationMessage) {
        throw new Error(validationMessage);
      }

      return normalizedRule;
    });
  }

  private normalizeImportedStringArray(value: unknown, fieldName: string): string[] {
    if (value === undefined || value === null) {
      return [];
    }

    if (!Array.isArray(value)) {
      throw new Error(`Imported feature flag ${fieldName} must be an array of strings.`);
    }

    const items = value.map((item) => {
      if (typeof item !== 'string' || !item.trim()) {
        throw new Error(`Imported feature flag ${fieldName} must contain only non-empty strings.`);
      }

      return item.trim();
    });

    return this.uniqueValues(items);
  }

  private normalizeImportedSchemaVersion(value: unknown): number {
    if (value === undefined || value === null) {
      return DEFAULT_SCHEMA_VERSION;
    }

    if (value !== DEFAULT_SCHEMA_VERSION) {
      throw new Error('Imported feature flags must use schemaVersion 1.');
    }

    return DEFAULT_SCHEMA_VERSION;
  }

  private resetUserOverrideSelectionState(): void {
    this.userSearchQuery = '';
    this.userSearchResults = [];
    this.manualUserOverrideId = '';
    this.clearPendingUserOverrideSelection();
  }

  private captureEditorBaseline(): void {
    this.editorBaseline = this.editorStateFingerprint();
  }

  private editorStateFingerprint(): string {
    return JSON.stringify({
      id: this.editor.id || null,
      payload: {
        key: this.editor.key.trim().toUpperCase(),
        name: this.editor.name.trim(),
        description: this.editor.description.trim() || null,
        appTargets: this.uniqueValues(this.editor.appTargets),
        isActive: this.editor.isActive,
        defaultEnabled: this.editor.defaultEnabled,
        userOverrides: {
          includeUserIds: this.uniqueValues(this.editor.includeUserIds),
          excludeUserIds: this.uniqueValues(this.editor.excludeUserIds),
        },
        rules: this.editor.rules.map((rule) => this.toRulePayload(rule)),
        schemaVersion: DEFAULT_SCHEMA_VERSION,
      },
    });
  }

  private createRuleId(): string {
    return `rule-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
